import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { Badge, Button, Checkbox, Input, Modal, Tabs } from '../../components/primitives';
import { ListPage, ListTable, type ListColumn } from '../../components/list';
import { DocBlock, DocRow } from '../../components/document';
import {
  useVyuctovani,
  useCreateVyuctovani,
  useUpdateVyuctovani,
  useDeleteVyuctovani,
  useVyuctovaniShares,
  useAddVyuctovaniShare,
  useRemoveVyuctovaniShare,
  useSharedVyuctovani,
  useSetVyuctovaniOvereni,
  useSetVyuctovaniSchvaleni,
  useUpdateVyuctovaniUcetni,
  useVyuctovaniSouhrn,
  useAddVyuctovaniVklad,
  useRemoveVyuctovaniVklad,
  useAcceptVyuctovaniVklad,
  useRejectVyuctovaniVklad,
  useNavrhSmazaniVklad,
  useReakceSmazaniVklad,
  useNavrhSmazaniPolozka,
  useReakceSmazaniPolozka,
  useCreateVyuctovaniPrevod,
  useAcceptVyuctovaniPrevod,
  useRejectVyuctovaniPrevod,
  useCancelVyuctovaniPrevod,
  useSetVyuctovaniPrevodOvereni,
  useNotifikaceNastaveni,
  useUpdateNotifikaceNastaveni,
  useTestNotifikace,
} from '../../api/hooks';
import type { SharedVyuctovani, VyuctovaniVklad, VyuctovaniPrevod, NotifikaceNastaveni } from '../../api/schemas';
import { apiFetch, getAuthToken } from '../../lib/api';
import type { VyuctovaniPolozka } from '../../api/schemas';
import { enablePush, disablePush, getPushState, isPushSupported } from '../../lib/push';

const viewTabs = [
  { id: 'moje', label: 'Moje útraty' },
  { id: 'kontrola', label: 'Ke kontrole' },
];

/** Stáhne soubor z chráněného endpointu (přidá Bearer token). */
async function downloadFile(path: string, filename: string) {
  const token = getAuthToken();
  const res = await fetch(`/api${path}`, token ? { headers: { Authorization: `Bearer ${token}` } } : undefined);
  if (!res.ok) {
    window.alert('Export se nezdařil.');
    return;
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const escHtml = (s: string) => String(s).replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c] as string));

/** Sestaví jednu HTML tabulku (s volitelným nadpisem) pro PDF. */
function buildTableHtml(heading: string, headers: string[], rows: string[][], moneyCol: number, totalText: string): string {
  const th = headers.map((h, i) => `<th style="text-align:${i === moneyCol ? 'right' : 'left'}">${escHtml(h)}</th>`).join('');
  const tr = rows
    .map((r) => `<tr>${r.map((c, i) => `<td style="${i === moneyCol ? 'text-align:right;font-family:monospace' : ''}">${escHtml(c)}</td>`).join('')}</tr>`)
    .join('');
  const tfootCells = headers
    .map((_, i) => (i === moneyCol - 1 ? '<td style="text-align:right;font-weight:bold">Celkem</td>' : i === moneyCol ? `<td style="text-align:right;font-weight:bold;font-family:monospace">${escHtml(totalText)}</td>` : '<td></td>'))
    .join('');
  return `${heading ? `<h2>${escHtml(heading)}</h2>` : ''}<table><thead><tr>${th}</tr></thead><tbody>${tr}</tbody><tfoot><tr>${tfootCells}</tr></tfoot></table>`;
}

/** Obalí jednu či více tabulek do tiskového dokumentu (bez tiskového dialogu). */
function buildPrintDoc(title: string, ...tables: string[]): string {
  const dnes = new Date().toLocaleDateString('cs-CZ');
  return `<!doctype html><html lang="cs"><head><meta charset="utf-8"><title>${escHtml(title)}</title>
<style>
  body{font-family:Arial,Helvetica,sans-serif;margin:0;color:#1a1a1a}
  h1{font-size:18px;color:#12325a;margin:0 0 4px}
  h2{font-size:13px;color:#12325a;margin:18px 0 6px}
  .meta{color:#777;font-size:11px;margin-bottom:14px}
  table{width:100%;border-collapse:collapse;font-size:12px;margin-bottom:6px}
  th{background:#12325a;color:#fff;padding:7px 8px;text-align:left}
  td{border:1px solid #dde3ed;padding:5px 8px}
  tfoot td{border:none;padding-top:8px}
  @page{margin:14mm}
</style></head><body>
  <h1>${escHtml(title)}</h1>
  <div class="meta">Vyhotoveno ${escHtml(dnes)}</div>
  ${tables.filter(Boolean).join('\n')}
</body></html>`;
}

/** Vygeneruje čisté PDF přes backend (Playwright) a stáhne ho — BEZ záhlaví/zápatí prohlížeče. */
async function downloadPdf(html: string, filename: string) {
  try {
    const blob = (await apiFetch('/pdf/generate', { method: 'POST', body: { html, filename } })) as Blob;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch {
    window.alert('Nepodařilo se vygenerovat PDF.');
  }
}

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function fmtCastka(n: number, mena = 'CZK'): string {
  const s = (n ?? 0).toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${s} ${mena === 'CZK' ? 'Kč' : mena}`;
}

function fmtDatum(iso: string): string {
  const m = (iso || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}. ${m[2]}. ${m[1]}` : iso || '—';
}

/** Datum + čas potvrzení (z ISO timestampu prijato_at). */
function fmtDatumCas(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return fmtDatum(iso);
  return `${d.toLocaleDateString('cs-CZ')} ${d.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })}`;
}

/** Jeden řádek přijatých peněz (vklad účetní nebo příchozí převod). */
type PrijemRadek = { key: string; datum: string; castka: number; od: string; poznamka: string; prijato_at?: string | null; vklad?: VyuctovaniVklad };

/** Tabulka přijatých peněz — datum, od koho, částka, čas potvrzení + součet.
 * `renderAction` (volitelně, jen pro účetní) přidá akční sloupec na konec řádku. */
function PrijatePenizeTable({ rows, renderAction }: { rows: PrijemRadek[]; renderAction?: (r: PrijemRadek) => ReactNode }) {
  const celkem = rows.reduce((s, r) => s + (r.castka ?? 0), 0);
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ textAlign: 'left', color: 'var(--sd-text-dim)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            <th style={{ padding: '6px 12px', width: 100 }}>Datum</th>
            <th style={{ padding: '6px 12px' }}>Od koho</th>
            <th style={{ padding: '6px 12px', width: 120, textAlign: 'right' }}>Částka</th>
            <th style={{ padding: '6px 12px', width: 150 }}>Potvrzeno</th>
            {renderAction && <th style={{ padding: '6px 12px', width: 110, textAlign: 'center' }}>Smazat</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} style={{ borderTop: '1px solid var(--sd-w-efeae0, #efeae0)' }}>
              <td style={{ padding: '8px 12px', fontFamily: 'var(--sd-font-mono)', color: 'var(--sd-text-dim)' }}>{fmtDatum(r.datum)}</td>
              <td style={{ padding: '8px 12px' }}>
                {r.od}
                {r.poznamka && <span style={{ color: 'var(--sd-text-dim)' }}> · {r.poznamka}</span>}
              </td>
              <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'var(--sd-font-mono)', fontWeight: 600, color: '#1f8b4c' }}>{fmtCastka(r.castka)}</td>
              <td style={{ padding: '8px 12px', color: 'var(--sd-text-dim)', fontSize: 12 }}>{fmtDatumCas(r.prijato_at)}</td>
              {renderAction && <td style={{ padding: '8px 12px', textAlign: 'center' }}>{renderAction(r)}</td>}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: '2px solid #d9ecdf' }}>
            <td />
            <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700 }}>Celkem přijato</td>
            <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'var(--sd-font-mono)', fontWeight: 700, color: '#1f8b4c' }}>{fmtCastka(celkem)}</td>
            <td />
            {renderAction && <td />}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

/** Stav modalu „důvod odmítnutí" — sdílený více toky (odmítnutí příjmu i smazání). */
type ReasonState = { title: string; intro: string; submitLabel: string; pending?: boolean; run: (duvod: string) => void } | null;

/** Modal na zadání důvodu odmítnutí. Důvod je nepovinný, ale doporučený. */
function ReasonModal({ state, onClose }: { state: ReasonState; onClose: () => void }) {
  const [text, setText] = useState('');
  const open = !!state;
  // reset textu při každém otevření
  useEffect(() => { if (open) setText(''); }, [open]);
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={state?.title || 'Odmítnout'}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Zpět</Button>
          <Button
            variant="danger"
            disabled={!!state?.pending}
            onClick={() => { state?.run(text.trim()); setText(''); }}
          >
            {state?.pending ? 'Odesílám…' : state?.submitLabel || 'Odmítnout'}
          </Button>
        </>
      }
    >
      <div style={{ fontSize: 13, color: 'var(--sd-text-dim)', marginBottom: 10 }}>{state?.intro}</div>
      <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--sd-text-dim)' }}>Důvod odmítnutí
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          placeholder="Napište, proč odmítáte — uvidí to účetní."
          style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, fontSize: 13, fontFamily: 'inherit', resize: 'vertical', marginTop: 4 }}
        />
      </label>
    </Modal>
  );
}

export function VyuctovaniPage() {
  const [view, setView] = useState('moje');

  return (
    <ListPage
      breadcrumb={['Obchod', 'Vyúčtování']}
      title="Vyúčtování"
      subtitle="Útraty z firemních peněz · kontrola účetní"
      tools={<Tabs items={viewTabs} value={view} onChange={setView} />}
    >
      {view === 'moje' ? <MojeUtraty /> : <KeKontrole />}
    </ListPage>
  );
}

// ─── MOJE ÚTRATY (zaměstnanec) ───────────────────────────────────────────────

function MojeUtraty() {
  const { data: polozky = [], isLoading, error } = useVyuctovani();
  const { data: souhrn } = useVyuctovaniSouhrn();
  const create = useCreateVyuctovani();
  const update = useUpdateVyuctovani();
  const del = useDeleteVyuctovani();
  const acceptVklad = useAcceptVyuctovaniVklad();
  const rejectVklad = useRejectVyuctovaniVklad();
  const reakceSmazVklad = useReakceSmazaniVklad();
  const reakceSmazPolozka = useReakceSmazaniPolozka();
  const createPrevod = useCreateVyuctovaniPrevod();
  const acceptPrevod = useAcceptVyuctovaniPrevod();
  const rejectPrevod = useRejectVyuctovaniPrevod();
  const cancelPrevod = useCancelVyuctovaniPrevod();

  // Modal „důvod odmítnutí" — sdílený pro odmítnutí příjmu i smazání.
  const [reason, setReason] = useState<ReasonState>(null);

  // Předání peněz kolegovi
  const [prevodOpen, setPrevodOpen] = useState(false);
  const [pEmail, setPEmail] = useState('');
  const [pCastka, setPCastka] = useState('');
  const [pDatum, setPDatum] = useState(todayIso());
  const [pPoznamka, setPPoznamka] = useState('');
  const [pErr, setPErr] = useState<string | null>(null);

  function openPrevod() {
    setPEmail('');
    setPCastka('');
    setPDatum(todayIso());
    setPPoznamka('');
    setPErr(null);
    setPrevodOpen(true);
  }
  async function savePrevod() {
    const email = pEmail.trim();
    const castka = parseFloat(pCastka.replace(',', '.')) || 0;
    if (!email || castka <= 0) return;
    setPErr(null);
    try {
      await createPrevod.mutateAsync({ email, castka, datum: pDatum, poznamka: pPoznamka.trim() });
      setPrevodOpen(false);
    } catch (ex: unknown) {
      const status = (ex as { status?: number })?.status;
      setPErr(
        status === 404 ? 'Uživatel s tímto e-mailem nenalezen.'
          : status === 400 ? 'Zkontrolujte částku a e-mail (nelze předat sám sobě).'
          : 'Předání se nezdařilo.',
      );
    }
  }

  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [datum, setDatum] = useState(todayIso());
  const [popis, setPopis] = useState('');
  const [stavba, setStavba] = useState('');
  const [castka, setCastka] = useState('');

  const editingItem = editId != null ? polozky.find((x) => x.id === editId) ?? null : null;
  const locked = !!editingItem?.schvaleno; // schváleno účetní → zaměstnanec needituje

  function resetForm() {
    setEditId(null);
    setDatum(todayIso());
    setPopis('');
    setStavba('');
    setCastka('');
  }

  function openNew() {
    resetForm();
    setOpen(true);
  }

  function openEdit(p: VyuctovaniPolozka) {
    setEditId(p.id);
    setDatum(p.datum || todayIso());
    setPopis(p.popis);
    setStavba(p.stavba || '');
    setCastka(String(p.castka ?? ''));
    setOpen(true);
  }

  async function handleSave() {
    if (locked) return;
    const payload = {
      datum,
      popis: popis.trim(),
      stavba: stavba.trim(),
      castka: parseFloat(castka.replace(',', '.')) || 0,
    };
    if (editId) await update.mutateAsync({ id: editId, payload });
    else await create.mutateAsync(payload);
    setOpen(false);
    resetForm();
  }

  async function handleDelete() {
    if (!editId) return;
    if (!window.confirm('Opravdu smazat tuto útratu?')) return;
    await del.mutateAsync(editId);
    setOpen(false);
    resetForm();
  }

  // Odmítnutí přijetí vkladu (s důvodem pro účetní)
  function odmitnoutPrijem(v: VyuctovaniVklad) {
    setReason({
      title: 'Odmítnout přijetí peněz',
      intro: `Odmítáte přijetí ${fmtCastka(v.castka)}${v.poznamka ? ` (${v.poznamka})` : ''}. Do zůstatku se nezapočítá a účetní uvidí váš důvod.`,
      submitLabel: 'Odmítnout přijetí',
      run: (duvod) => { rejectVklad.mutate({ vkladId: v.id, duvod }); setReason(null); },
    });
  }

  // Odmítnutí přijetí převodu od kolegy (částka se vrátí odesílateli, důvod uvidí on i jeho účetní)
  function odmitnoutPrijemPrevod(p: VyuctovaniPrevod) {
    setReason({
      title: 'Odmítnout přijetí peněz',
      intro: `Odmítáte přijetí ${fmtCastka(p.castka)} od ${p.from_name || p.from_email}. Částka se vrátí odesílateli a uvidí váš důvod.`,
      submitLabel: 'Odmítnout přijetí',
      run: (duvod) => { rejectPrevod.mutate({ prevodId: p.id, duvod }); setReason(null); },
    });
  }

  // Reakce na návrh smazání od účetní
  function souhlasSeSmazanim(kind: 'vklad' | 'polozka', id: number, label: string) {
    if (!window.confirm(`Opravdu smazat „${label}"? Záznam zmizí oboma stranám.`)) return;
    if (kind === 'vklad') reakceSmazVklad.mutate({ vkladId: id, souhlas: true });
    else reakceSmazPolozka.mutate({ id, souhlas: true });
  }
  function odmitnoutSmazani(kind: 'vklad' | 'polozka', id: number, label: string) {
    setReason({
      title: 'Odmítnout smazání',
      intro: `Odmítáte smazání „${label}". Záznam zůstane a účetní uvidí váš důvod.`,
      submitLabel: 'Odmítnout smazání',
      run: (duvod) => {
        if (kind === 'vklad') reakceSmazVklad.mutate({ vkladId: id, souhlas: false, duvod });
        else reakceSmazPolozka.mutate({ id, souhlas: false, duvod });
        setReason(null);
      },
    });
  }

  const utratyKeSmazani = polozky.filter((p) => p.smazani_navrzeno);
  const vkladyKeSmazani = (souhrn?.vklady ?? []).filter((v) => v.smazani_navrzeno);

  const celkem = polozky.reduce((s, p) => s + (p.castka ?? 0), 0);
  const pocetOvereno = polozky.filter((p) => p.overeno).length;

  // Přijaté peníze: přijaté vklady účetní/firmy + přijaté příchozí převody od kolegů
  const prijatePenize: PrijemRadek[] = [
    ...(souhrn?.vklady ?? [])
      .filter((v) => v.prijato)
      .map((v) => ({ key: `v${v.id}`, datum: v.datum, castka: v.castka, od: 'Účetní / firma', poznamka: v.poznamka, prijato_at: v.prijato_at })),
    ...(souhrn?.prevody_prichozi ?? [])
      .filter((p) => p.prijato)
      .map((p) => ({ key: `p${p.id}`, datum: p.datum, castka: p.castka, od: p.from_name || p.from_email, poznamka: p.poznamka, prijato_at: p.prijato_at })),
  ].sort((a, b) => (a.datum < b.datum ? 1 : a.datum > b.datum ? -1 : 0));

  function handlePdf() {
    const rows = polozky.map((p) => [
      fmtDatum(p.datum),
      p.stavba || '—',
      p.popis || '—',
      fmtCastka(p.castka, p.mena),
      p.overeno ? 'Ano' : 'Ne',
    ]);
    const utratyTable = buildTableHtml('Útraty', ['Datum', 'Stavba', 'Za co', 'Částka', 'Sedí s dokladem'], rows, 3, fmtCastka(celkem));
    let prijateTable = '';
    if (prijatePenize.length) {
      const prRows = prijatePenize.map((r) => [fmtDatum(r.datum), r.od + (r.poznamka ? ` · ${r.poznamka}` : ''), fmtCastka(r.castka), fmtDatumCas(r.prijato_at)]);
      const prCelkem = prijatePenize.reduce((s, r) => s + (r.castka ?? 0), 0);
      prijateTable = buildTableHtml('Přijaté peníze', ['Datum', 'Od koho', 'Částka', 'Potvrzeno'], prRows, 2, fmtCastka(prCelkem));
    }
    const html = buildPrintDoc('Vyúčtování — moje útraty', prijateTable, utratyTable);
    downloadPdf(html, 'vyuctovani.pdf');
  }

  const columns: ListColumn<VyuctovaniPolozka>[] = [
    { key: 'datum', label: 'Datum', width: '110px', mono: true, dim: true, render: (p) => fmtDatum(p.datum) },
    { key: 'stavba', label: 'Stavba', width: '180px', dim: true, render: (p) => p.stavba || '—' },
    { key: 'popis', label: 'Za co', primary: true, render: (p) => p.popis || '—' },
    { key: 'castka', label: 'Částka', width: '130px', mono: true, align: 'right', render: (p) => fmtCastka(p.castka, p.mena) },
    {
      key: 'stav',
      label: 'Stav',
      width: '160px',
      render: (p) =>
        p.schvaleno ? (
          <Badge tone="success" pill dot>🔒 Odsouhlaseno</Badge>
        ) : p.overeno ? (
          <Badge tone="success" pill dot>Sedí s dokladem</Badge>
        ) : (
          <Badge tone="neutral" pill>Nezkontrolováno</Badge>
        ),
    },
  ];

  const zustatek = souhrn?.zustatek ?? 0;

  return (
    <>
      {souhrn && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 14, padding: '12px 16px', borderRadius: 6, border: '1px solid', borderColor: zustatek < 0 ? 'var(--sd-danger, #c0392b)' : '#bfe0c8', background: zustatek < 0 ? 'var(--sd-danger-soft, #fdecea)' : '#eef7f0' }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--sd-text-dim)' }}>Zůstatek firemních peněz</div>
            <div style={{ fontSize: 22, fontWeight: 800, fontFamily: 'var(--sd-font-mono)', color: zustatek < 0 ? 'var(--sd-danger, #c0392b)' : '#1f8b4c' }}>{fmtCastka(zustatek)}</div>
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--sd-text-dim)', textAlign: 'right' }}>
            <div>vloženo účetní <strong style={{ color: 'var(--sd-doc-ink)' }}>{fmtCastka(souhrn.vklady_celkem)}</strong></div>
            {souhrn.prevody_in_celkem > 0 && (
              <div>přijato od kolegů <strong style={{ color: 'var(--sd-doc-ink)' }}>{fmtCastka(souhrn.prevody_in_celkem)}</strong></div>
            )}
            <div>− útraty <strong style={{ color: 'var(--sd-doc-ink)' }}>{fmtCastka(souhrn.utraty_celkem)}</strong></div>
            {souhrn.prevody_out_celkem > 0 && (
              <div>− předáno kolegům <strong style={{ color: 'var(--sd-doc-ink)' }}>{fmtCastka(souhrn.prevody_out_celkem)}</strong></div>
            )}
            {souhrn.vklady_neprijate_celkem > 0 && (
              <div style={{ color: 'var(--sd-warning, #b8860b)' }}>čeká na vaše přijetí <strong>{fmtCastka(souhrn.vklady_neprijate_celkem)}</strong></div>
            )}
          </div>
        </div>
      )}

      <SmazaniKPotvrzeni
        utraty={utratyKeSmazani}
        vklady={vkladyKeSmazani}
        onAccept={souhlasSeSmazanim}
        onReject={odmitnoutSmazani}
      />

      <VkladyKPrijeti
        vklady={(souhrn?.vklady ?? []).filter((v) => !v.prijato && !v.odmitnuto)}
        onAccept={(id) => acceptVklad.mutate({ vkladId: id, prijato: true })}
        onReject={(id) => { const v = (souhrn?.vklady ?? []).find((x) => x.id === id); if (v) odmitnoutPrijem(v); }}
        pending={acceptVklad.isPending}
      />

      <PrevodyKPrijeti
        prevody={(souhrn?.prevody_prichozi ?? []).filter((p) => !p.prijato && !p.odmitnuto)}
        onAccept={(id) => acceptPrevod.mutate({ prevodId: id, prijato: true })}
        onReject={(id) => { const p = (souhrn?.prevody_prichozi ?? []).find((x) => x.id === id); if (p) odmitnoutPrijemPrevod(p); }}
        pending={acceptPrevod.isPending}
      />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
        <div style={{ fontSize: 13, color: 'var(--sd-text-dim)' }}>
          {isLoading ? 'Načítám…' : error ? 'Chyba načítání' : `${polozky.length} položek · ověřeno ${pocetOvereno}/${polozky.length} · celkem `}
          {!isLoading && !error && <strong style={{ color: 'var(--sd-doc-ink)' }}>{fmtCastka(celkem)}</strong>}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button variant="secondary" onClick={handlePdf} disabled={!polozky.length}>📄 PDF</Button>
          <Button variant="secondary" onClick={() => downloadFile('/vyuctovani/export.xlsx', 'vyuctovani.xlsx')} disabled={!polozky.length}>⬇ Excel</Button>
          <Button variant="secondary" onClick={openPrevod}>↗ Předat peníze</Button>
          <Button variant="primary" onClick={openNew}>+ Nová útrata</Button>
        </div>
      </div>

      <ListTable
        columns={columns}
        rows={polozky}
        rowKey={(p) => p.id}
        onRowClick={openEdit}
        emptyTitle="Žádné útraty"
        emptyText="Zapsané útraty z firemních peněz se objeví zde."
      />

      {prijatePenize.length > 0 && (
        <div style={{ marginTop: 18, border: '1px solid #bfe0c8', borderRadius: 4, background: '#fff', overflow: 'hidden' }}>
          <div style={{ padding: '10px 14px', borderBottom: '1px solid #d9ecdf', background: '#eef7f0' }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: '#1f8b4c', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Přijaté peníze</div>
            <div style={{ fontSize: 12, color: 'var(--sd-text-dim)', marginTop: 2 }}>
              Firemní peníze, které jste přijali — od účetní/firmy i od kolegů. Slouží jako doklad, kdy a v jaké výši peníze přišly.
            </div>
          </div>
          <PrijatePenizeTable rows={prijatePenize} />
        </div>
      )}

      <PredanePenize
        prevody={souhrn?.prevody_odchozi ?? []}
        onCancel={(id) => {
          if (window.confirm('Zrušit tento převod? Částka se vám vrátí do zůstatku.')) cancelPrevod.mutate(id);
        }}
        pending={cancelPrevod.isPending}
      />

      <SharePanel />

      <NotifikacePanel />

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={locked ? 'Útrata (odsouhlaseno)' : editId ? 'Upravit útratu' : 'Nová útrata'}
        size="md"
        footer={
          locked ? (
            <Button variant="ghost" onClick={() => setOpen(false)}>Zavřít</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setOpen(false)}>Zrušit</Button>
              {editId && (
                <Button variant="danger" onClick={handleDelete} disabled={del.isPending}>
                  {del.isPending ? 'Mažu…' : '🗑️ Smazat'}
                </Button>
              )}
              <Button variant="primary" onClick={handleSave} disabled={create.isPending || update.isPending || !popis.trim()}>
                {create.isPending || update.isPending ? 'Ukládám…' : editId ? 'Uložit změny' : 'Přidat'}
              </Button>
            </>
          )
        }
      >
        {locked && (
          <div style={{ marginBottom: 12, padding: '10px 12px', borderRadius: 4, background: 'var(--sd-success-soft, #e7f6ec)', color: 'var(--sd-success, #1f8b4c)', fontSize: 13, fontWeight: 600 }}>
            🔒 Tuto útratu už odsouhlasila účetní — úpravy může provádět jen účetní.
          </div>
        )}
        <div style={{ border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, background: '#fff', overflow: 'hidden' }}>
          <DocBlock idx="01" title="Útrata z firemních peněz">
            <DocRow
              k="Datum *"
              v={
                <input
                  type="date"
                  value={datum}
                  disabled={locked}
                  onChange={(e) => setDatum(e.target.value)}
                  style={{ padding: '3px 6px', border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, background: locked ? 'var(--sd-w-f4f2ec, #f4f2ec)' : '#fff', color: 'var(--sd-doc-ink)', fontSize: 13, fontWeight: 600 }}
                  aria-label="Datum"
                />
              }
            />
            <DocRow k="Stavba" value={stavba} onChange={locked ? undefined : setStavba} v={locked ? (stavba || '—') : undefined} placeholder="které stavby/zakázky se útrata týká" />
            <DocRow k="Za co to bylo *" multiline value={popis} onChange={locked ? undefined : setPopis} v={locked ? (popis || '—') : undefined} placeholder="materiál — hobby market, nafta, drobné nářadí…" />
            <DocRow
              k="Částka (Kč) *"
              v={
                <input
                  type="text"
                  inputMode="decimal"
                  value={castka}
                  disabled={locked}
                  onChange={(e) => setCastka(e.target.value)}
                  placeholder="0,00"
                  style={{ padding: '3px 6px', border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, background: locked ? 'var(--sd-w-f4f2ec, #f4f2ec)' : '#fff', color: 'var(--sd-doc-ink)', fontSize: 13, fontWeight: 600, textAlign: 'right', fontFamily: 'var(--sd-font-mono)', width: 140 }}
                  aria-label="Částka"
                />
              }
            />
            {editingItem?.overeno && (
              <DocRow
                k="Stav dokladu"
                v={<Badge tone="success" pill dot>Účetní ověřila — sedí s dokladem</Badge>}
              />
            )}
          </DocBlock>
        </div>
        {(create.isError || update.isError) && (
          <div style={{ marginTop: 12, padding: 12, borderRadius: 4, background: 'var(--sd-danger-soft)', color: 'var(--sd-danger)', fontSize: 13 }}>
            Uložení selhalo. Zkuste to prosím znovu.
          </div>
        )}
      </Modal>

      <Modal
        open={prevodOpen}
        onClose={() => setPrevodOpen(false)}
        title="Předat firemní peníze kolegovi"
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setPrevodOpen(false)}>Zrušit</Button>
            <Button variant="primary" onClick={savePrevod} disabled={createPrevod.isPending || !pEmail.trim() || !pCastka.trim()}>
              {createPrevod.isPending ? 'Předávám…' : '↗ Předat peníze'}
            </Button>
          </>
        }
      >
        <div style={{ marginBottom: 12, fontSize: 12.5, color: 'var(--sd-text-dim)' }}>
          Částka se z vašeho zůstatku odečte hned. Kolegovi se připíše až poté, co převod ve své aplikaci přijme.
          Účetní převod uvidí ve vašem výpisu ke kontrole.
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--sd-text-dim)' }}>E-mail příjemce *
            <Input type="email" value={pEmail} onChange={(e) => setPEmail(e.target.value)} placeholder="e-mail kolegy" />
          </label>
          <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--sd-text-dim)' }}>Částka (Kč) *
            <input type="text" inputMode="decimal" value={pCastka} onChange={(e) => setPCastka(e.target.value)} placeholder="0,00"
              style={{ padding: '5px 8px', border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, fontSize: 13, width: '100%', textAlign: 'right', fontFamily: 'var(--sd-font-mono)' }} />
          </label>
          <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--sd-text-dim)' }}>Datum předání
            <input type="date" value={pDatum} onChange={(e) => setPDatum(e.target.value)}
              style={{ padding: '5px 8px', border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, fontSize: 13, width: '100%' }} />
          </label>
          <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--sd-text-dim)' }}>Poznámka
            <input type="text" value={pPoznamka} onChange={(e) => setPPoznamka(e.target.value)} placeholder="např. na nákup materiálu"
              style={{ padding: '5px 8px', border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, fontSize: 13, width: '100%' }} />
          </label>
        </div>
        {pErr && <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--sd-danger)' }}>{pErr}</div>}
      </Modal>

      <ReasonModal state={reason} onClose={() => setReason(null)} />
    </>
  );
}

// ─── ŽÁDOSTI O SMAZÁNÍ OD ÚČETNÍ (zaměstnanec potvrzuje / odmítá) ─────────────

function SmazaniKPotvrzeni({
  utraty, vklady, onAccept, onReject,
}: {
  utraty: VyuctovaniPolozka[];
  vklady: VyuctovaniVklad[];
  onAccept: (kind: 'vklad' | 'polozka', id: number, label: string) => void;
  onReject: (kind: 'vklad' | 'polozka', id: number, label: string) => void;
}) {
  if (utraty.length === 0 && vklady.length === 0) return null;
  type Radek = { kind: 'vklad' | 'polozka'; id: number; label: string; detail: string; castka: number };
  const radky: Radek[] = [
    ...utraty.map((p) => ({ kind: 'polozka' as const, id: p.id, label: p.popis || 'útrata', detail: `útrata · ${fmtDatum(p.datum)}${p.stavba ? ` · ${p.stavba}` : ''}`, castka: p.castka })),
    ...vklady.map((v) => ({ kind: 'vklad' as const, id: v.id, label: v.poznamka || 'vklad od účetní', detail: `vklad od účetní · ${fmtDatum(v.datum)}`, castka: v.castka })),
  ];
  return (
    <div style={{ marginBottom: 14, border: '1px solid var(--sd-danger, #e0a0a0)', borderRadius: 6, background: 'var(--sd-danger-soft, #fdecea)', overflow: 'hidden' }}>
      <div style={{ padding: '10px 14px', borderBottom: '1px solid #f3d2cd' }}>
        <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--sd-danger, #b33636)' }}>Žádost o smazání od účetní</div>
        <div style={{ fontSize: 12.5, color: 'var(--sd-text-dim)', marginTop: 2 }}>
          Účetní navrhla smazání těchto záznamů. Smažou se až po vašem potvrzení. Pokud nesouhlasíte, odmítněte a napište důvod — účetní ho uvidí.
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {radky.map((r) => (
          <div key={`${r.kind}${r.id}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', padding: '10px 14px', borderTop: '1px solid #f3d8d3' }}>
            <div style={{ fontSize: 13 }}>
              <strong style={{ fontFamily: 'var(--sd-font-mono)', fontSize: 15 }}>{fmtCastka(r.castka)}</strong>
              <span style={{ color: 'var(--sd-text-dim)' }}> · {r.label} · {r.detail}</span>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="ghost" size="sm" onClick={() => onReject(r.kind, r.id, r.label)}>Odmítnout</Button>
              <Button variant="danger" size="sm" onClick={() => onAccept(r.kind, r.id, r.label)}>🗑️ Smazat</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── PENÍZE K PŘIJETÍ OD KOLEGY (příjemce potvrzuje převod) ──────────────────

function PrevodyKPrijeti({ prevody, onAccept, onReject, pending }: { prevody: VyuctovaniPrevod[]; onAccept: (id: number) => void; onReject: (id: number) => void; pending: boolean }) {
  if (prevody.length === 0) return null;
  return (
    <div style={{ marginBottom: 14, border: '1px solid var(--sd-warning, #e0c068)', borderRadius: 6, background: 'var(--sd-warning-soft, #fff8e6)', overflow: 'hidden' }}>
      <div style={{ padding: '10px 14px', borderBottom: '1px solid #efe2bf' }}>
        <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--sd-warning, #8a6d1b)' }}>Peníze k přijetí od kolegy</div>
        <div style={{ fontSize: 12.5, color: 'var(--sd-text-dim)', marginTop: 2 }}>
          Kolega vám předal firemní peníze. Potvrďte přijetí — do vašeho zůstatku se započítají až po vašem souhlasu. Pokud částka nesedí, nepřijímejte a obraťte se na kolegu.
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {prevody.map((p) => (
          <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 14px', borderTop: '1px solid var(--sd-w-f1e8cf, #f1e8cf)' }}>
            <div style={{ fontSize: 13 }}>
              <strong style={{ fontFamily: 'var(--sd-font-mono)', fontSize: 15 }}>{fmtCastka(p.castka)}</strong>
              <span style={{ color: 'var(--sd-text-dim)' }}> · od {p.from_name || p.from_email} · {fmtDatum(p.datum)}{p.poznamka ? ` · ${p.poznamka}` : ''}</span>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="ghost" size="sm" onClick={() => onReject(p.id)} disabled={pending}>Odmítnout</Button>
              <Button variant="primary" size="sm" onClick={() => onAccept(p.id)} disabled={pending}>
                {pending ? 'Ukládám…' : '✓ Přijmout'}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── PŘEDANÉ PENÍZE (odchozí převody odesílatele) ────────────────────────────

function PredanePenize({ prevody, onCancel, pending }: { prevody: VyuctovaniPrevod[]; onCancel: (id: number) => void; pending: boolean }) {
  if (prevody.length === 0) return null;
  return (
    <div style={{ marginTop: 18, border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, background: '#fff', overflow: 'hidden' }}>
      <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--sd-w-e7e1d4, #e7e1d4)', background: 'var(--sd-w-f7f4ee, #f7f4ee)' }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--sd-text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Předané peníze</div>
        <div style={{ fontSize: 12, color: 'var(--sd-text-dim)', marginTop: 2 }}>
          Peníze, které jste předali kolegům. Odečetly se z vašeho zůstatku hned. Nepřijatý převod můžete zrušit — částka se vrátí.
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: 'left', color: 'var(--sd-text-dim)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              <th style={{ padding: '6px 12px', width: 100 }}>Datum</th>
              <th style={{ padding: '6px 12px' }}>Komu</th>
              <th style={{ padding: '6px 12px', width: 120, textAlign: 'right' }}>Částka</th>
              <th style={{ padding: '6px 12px', width: 200 }}>Stav</th>
              <th style={{ padding: '6px 12px', width: 44 }} />
            </tr>
          </thead>
          <tbody>
            {prevody.map((p) => (
              <tr key={p.id} style={{ borderTop: '1px solid var(--sd-w-efeae0, #efeae0)' }}>
                <td style={{ padding: '8px 12px', fontFamily: 'var(--sd-font-mono)', color: 'var(--sd-text-dim)' }}>{fmtDatum(p.datum)}</td>
                <td style={{ padding: '8px 12px' }}>
                  {p.to_name || p.to_email}
                  {p.poznamka && <span style={{ color: 'var(--sd-text-dim)' }}> · {p.poznamka}</span>}
                </td>
                <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'var(--sd-font-mono)', fontWeight: 600 }}>{fmtCastka(p.castka)}</td>
                <td style={{ padding: '8px 12px' }}>
                  {p.odmitnuto ? (
                    <>
                      <Badge tone="danger" pill dot>Odmítnuto příjemcem</Badge>
                      {p.duvod_odmitnuti && <div style={{ fontSize: 11.5, color: 'var(--sd-danger, #c0392b)', marginTop: 2 }}>důvod: {p.duvod_odmitnuti}</div>}
                    </>
                  ) : p.prijato ? (
                    <Badge tone="success" pill dot>{p.overeno ? '🔒 Zkontrolováno' : 'Přijato kolegou'}</Badge>
                  ) : (
                    <Badge tone="neutral" pill>Čeká na přijetí</Badge>
                  )}
                </td>
                <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                  {!p.prijato && (
                    <button type="button" onClick={() => onCancel(p.id)} disabled={pending} title={p.odmitnuto ? 'Odebrat odmítnutý převod' : 'Zrušit převod'}
                      style={{ background: 'none', border: 'none', color: 'var(--sd-danger, #c0392b)', cursor: 'pointer', fontWeight: 700, fontSize: 15 }}>✕</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── PENÍZE K PŘIJETÍ (zaměstnanec potvrzuje vklad účetní) ───────────────────

function VkladyKPrijeti({ vklady, onAccept, onReject, pending }: { vklady: VyuctovaniVklad[]; onAccept: (id: number) => void; onReject: (id: number) => void; pending: boolean }) {
  if (vklady.length === 0) return null;
  return (
    <div style={{ marginBottom: 14, border: '1px solid var(--sd-warning, #e0c068)', borderRadius: 6, background: 'var(--sd-warning-soft, #fff8e6)', overflow: 'hidden' }}>
      <div style={{ padding: '10px 14px', borderBottom: '1px solid #efe2bf' }}>
        <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--sd-warning, #8a6d1b)' }}>Peníze k přijetí</div>
        <div style={{ fontSize: 12.5, color: 'var(--sd-text-dim)', marginTop: 2 }}>
          Účetní vám předala firemní peníze. Potvrďte přijetí — do zůstatku se započítají až po vašem souhlasu. Pokud částka nesedí, nepřijímejte a obraťte se na účetní.
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {vklady.map((v) => (
          <div key={v.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 14px', borderTop: '1px solid var(--sd-w-f1e8cf, #f1e8cf)' }}>
            <div style={{ fontSize: 13 }}>
              <strong style={{ fontFamily: 'var(--sd-font-mono)', fontSize: 15 }}>{fmtCastka(v.castka)}</strong>
              <span style={{ color: 'var(--sd-text-dim)' }}> · {fmtDatum(v.datum)}{v.poznamka ? ` · ${v.poznamka}` : ''}</span>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="ghost" size="sm" onClick={() => onReject(v.id)} disabled={pending}>Odmítnout</Button>
              <Button variant="primary" size="sm" onClick={() => onAccept(v.id)} disabled={pending}>
                {pending ? 'Ukládám…' : '✓ Přijmout'}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── SDÍLENÍ S ÚČETNÍ ────────────────────────────────────────────────────────

function SharePanel() {
  const { data: shares = [] } = useVyuctovaniShares();
  const add = useAddVyuctovaniShare();
  const remove = useRemoveVyuctovaniShare();
  const [email, setEmail] = useState('');
  const [err, setErr] = useState<string | null>(null);

  async function handleAdd() {
    const e = email.trim();
    if (!e) return;
    setErr(null);
    try {
      await add.mutateAsync(e);
      setEmail('');
    } catch (ex: unknown) {
      const status = (ex as { status?: number })?.status;
      setErr(
        status === 404 ? 'Uživatel s tímto e-mailem nenalezen.'
          : status === 409 ? 'S tímto účtem už je nasdíleno.'
          : status === 400 ? 'Nelze sdílet sám se sebou.'
          : 'Sdílení se nezdařilo.',
      );
    }
  }

  return (
    <div style={{ marginTop: 18, border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, background: '#fff', padding: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--sd-text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
        Sdílet s účetní
      </div>
      <div style={{ fontSize: 12, color: 'var(--sd-text-dim)', marginBottom: 10 }}>
        Účetní s tímto účtem uvidí všechny vaše útraty (i ty budoucí) a bude moci odfajfkovat, že sedí s dokladem. Stačí nasdílet jednou.
      </div>

      {shares.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
          {shares.map((s) => (
            <div key={s.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '6px 10px', background: 'var(--sd-w-f7f4ee, #f7f4ee)', border: '1px solid var(--sd-w-e7e1d4, #e7e1d4)', borderRadius: 4 }}>
              <span style={{ fontSize: 13 }}>
                <strong>{s.shared_with_name || s.shared_with_email}</strong>
                {s.shared_with_name && <span style={{ color: 'var(--sd-text-dim)' }}> · {s.shared_with_email}</span>}
              </span>
              <Button size="sm" variant="ghost" onClick={() => remove.mutate(s.id)} disabled={remove.isPending}>
                Zrušit
              </Button>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="e-mail účetní"
            onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
          />
        </div>
        <Button variant="secondary" onClick={handleAdd} disabled={add.isPending || !email.trim()}>
          {add.isPending ? 'Sdílím…' : 'Sdílet'}
        </Button>
      </div>
      {err && <div style={{ marginTop: 8, fontSize: 12.5, color: 'var(--sd-danger)' }}>{err}</div>}
    </div>
  );
}

// ─── NOTIFIKACE (volitelné upozornění na události Vyúčtování) ─────────────────

const EVENT_OPTS: { key: keyof NotifikaceNastaveni; label: string; hint: string }[] = [
  { key: 'prijate_penize', label: 'Přijaté peníze', hint: 'Když mi účetní vloží firemní peníze nebo mi kolega pošle převod.' },
  { key: 'vydane_penize', label: 'Nové útraty (pro účetní)', hint: 'Když zaměstnanec, kterého kontroluji, přidá novou útratu.' },
  { key: 'potvrzeni', label: 'Potvrzení a odmítnutí', hint: 'Když příjemce potvrdí nebo odmítne můj vklad či převod.' },
  { key: 'kontrola', label: 'Kontrola mých položek', hint: 'Když účetní odsouhlasí mou položku nebo navrhne její smazání.' },
];

function NotifikacePanel() {
  const { data: nast } = useNotifikaceNastaveni();
  const update = useUpdateNotifikaceNastaveni();
  const test = useTestNotifikace();
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const pushSupported = isPushSupported();

  useEffect(() => { getPushState().then(setPushOn).catch(() => {}); }, []);

  if (!nast) return null;

  const save = (patch: Partial<NotifikaceNastaveni>) => update.mutate({ ...nast, ...patch });
  const anyEvent = EVENT_OPTS.some((o) => nast[o.key]);

  async function togglePush(on: boolean) {
    setMsg(null);
    setPushBusy(true);
    try {
      if (on) {
        await enablePush();
        setPushOn(true);
        save({ kanal_push: true });
      } else {
        await disablePush();
        setPushOn(false);
        save({ kanal_push: false });
      }
    } catch (e: unknown) {
      setMsg((e as Error)?.message || 'Push notifikace se nepodařilo zapnout.');
    } finally {
      setPushBusy(false);
    }
  }

  async function handleTest() {
    setMsg(null);
    try {
      await test.mutateAsync();
      setMsg('Zkušební notifikace odeslána. Pokud nedorazí, zkontrolujte zapnutou událost a kanál.');
    } catch {
      setMsg('Zkušební notifikaci se nepodařilo odeslat.');
    }
  }

  const labelCss: CSSProperties = { fontSize: 11, fontWeight: 800, color: 'var(--sd-text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 };

  return (
    <div style={{ marginTop: 18, border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, background: '#fff', padding: 14 }}>
      <div style={labelCss}>Notifikace</div>
      <div style={{ fontSize: 12, color: 'var(--sd-text-dim)', marginBottom: 12 }}>
        Vyberte, na co chcete být upozorněni a kam. Ve výchozím stavu je vše vypnuté.
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
        {EVENT_OPTS.map((o) => (
          <div key={o.key}>
            <Checkbox
              label={o.label}
              checked={!!nast[o.key]}
              onChange={(e) => save({ [o.key]: e.target.checked } as Partial<NotifikaceNastaveni>)}
            />
            <div style={{ fontSize: 11.5, color: 'var(--sd-text-dim)', marginLeft: 26, marginTop: 1 }}>{o.hint}</div>
          </div>
        ))}
      </div>

      <div style={labelCss}>Kam doručit</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 4, opacity: anyEvent ? 1 : 0.55 }}>
        <Checkbox
          label="E-mailem"
          checked={nast.kanal_email}
          onChange={(e) => save({ kanal_email: e.target.checked })}
        />
        <div>
          <Checkbox
            label={pushBusy ? 'Push — pracuji…' : 'Do telefonu / aplikace (push)'}
            checked={nast.kanal_push && pushOn}
            disabled={!pushSupported || pushBusy}
            onChange={(e) => togglePush(e.target.checked)}
          />
          <div style={{ fontSize: 11.5, color: 'var(--sd-text-dim)', marginLeft: 26, marginTop: 1 }}>
            {pushSupported
              ? 'Oznámení vyskočí, i když aplikaci nemáte otevřenou. Funguje po „Přidat na plochu" (PWA), na iPhonu jen v nainstalované aplikaci.'
              : 'Tento prohlížeč push notifikace nepodporuje.'}
          </div>
        </div>
      </div>

      <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button size="sm" variant="ghost" onClick={handleTest} disabled={test.isPending}>
          {test.isPending ? 'Odesílám…' : 'Poslat zkušební notifikaci'}
        </Button>
      </div>
      {msg && <div style={{ marginTop: 8, fontSize: 12.5, color: 'var(--sd-text-dim)' }}>{msg}</div>}
    </div>
  );
}

// ─── KE KONTROLE (účetní) ────────────────────────────────────────────────────

function KeKontrole() {
  const { data: groups = [], isLoading, error } = useSharedVyuctovani();
  const setOvereni = useSetVyuctovaniOvereni();
  const setSchvaleni = useSetVyuctovaniSchvaleni();
  const updateUcetni = useUpdateVyuctovaniUcetni();
  const addVklad = useAddVyuctovaniVklad();
  const removeVklad = useRemoveVyuctovaniVklad();
  const navrhSmazPolozka = useNavrhSmazaniPolozka();
  const navrhSmazVklad = useNavrhSmazaniVklad();
  const setPrevodOvereni = useSetVyuctovaniPrevodOvereni();

  // Účetní navrhne smazání útraty (potvrzuje zaměstnanec) / stáhne návrh.
  function navrhnoutSmazaniPolozky(p: VyuctovaniPolozka) {
    if (p.smazani_navrzeno) {
      navrhSmazPolozka.mutate({ id: p.id, navrzeno: false });
      return;
    }
    if (window.confirm(`Navrhnout smazání útraty „${p.popis || ''}"? Smaže se až po potvrzení zaměstnance.`)) {
      navrhSmazPolozka.mutate({ id: p.id, navrzeno: true });
    }
  }

  // Účetní navrhne smazání vkladu (potvrzuje zaměstnanec) / stáhne návrh.
  function navrhnoutSmazaniVkladu(v: VyuctovaniVklad) {
    if (v.smazani_navrzeno) {
      navrhSmazVklad.mutate({ vkladId: v.id, navrzeno: false });
      return;
    }
    if (window.confirm(`Navrhnout smazání vkladu ${fmtCastka(v.castka)}? Smaže se až po potvrzení zaměstnance.`)) {
      navrhSmazVklad.mutate({ vkladId: v.id, navrzeno: true });
    }
  }

  const [edit, setEdit] = useState<VyuctovaniPolozka | null>(null);
  const [eDatum, setEDatum] = useState('');
  const [ePopis, setEPopis] = useState('');
  const [eStavba, setEStavba] = useState('');
  const [eCastka, setECastka] = useState('');

  const [vkladFor, setVkladFor] = useState<SharedVyuctovani | null>(null);
  const [vCastka, setVCastka] = useState('');
  const [vDatum, setVDatum] = useState(todayIso());
  const [vPoznamka, setVPoznamka] = useState('');

  // přepínání mezi pracovníky: levý seznam + filtr
  const [query, setQuery] = useState('');
  const [onlyPending, setOnlyPending] = useState(false);
  const [selected, setSelected] = useState<number | 'all'>('all');

  function openVklad(g: SharedVyuctovani) {
    setVkladFor(g);
    setVCastka('');
    setVDatum(todayIso());
    setVPoznamka('');
  }
  async function saveVklad() {
    if (!vkladFor) return;
    await addVklad.mutateAsync({
      ownerId: vkladFor.owner_id,
      castka: parseFloat(vCastka.replace(',', '.')) || 0,
      datum: vDatum,
      poznamka: vPoznamka.trim(),
    });
    setVkladFor(null);
  }

  function openEdit(p: VyuctovaniPolozka) {
    setEdit(p);
    setEDatum(p.datum || todayIso());
    setEPopis(p.popis);
    setEStavba(p.stavba || '');
    setECastka(String(p.castka ?? ''));
  }
  async function saveEdit() {
    if (!edit) return;
    await updateUcetni.mutateAsync({
      id: edit.id,
      payload: { datum: eDatum, popis: ePopis.trim(), stavba: eStavba.trim(), castka: parseFloat(eCastka.replace(',', '.')) || 0 },
    });
    setEdit(null);
  }

  function handlePdf() {
    const rows: string[][] = [];
    let total = 0;
    groups.forEach((g) =>
      g.polozky.forEach((p) => {
        total += p.castka ?? 0;
        rows.push([g.owner_name || g.owner_email, p.stavba || '—', fmtDatum(p.datum), p.popis || '—', fmtCastka(p.castka, p.mena), p.overeno ? 'Ano' : 'Ne', p.schvaleno ? 'Ano' : 'Ne']);
      }),
    );
    const utratyTable = buildTableHtml('Útraty', ['Zaměstnanec', 'Stavba', 'Datum', 'Za co', 'Částka', 'Sedí s dokladem', 'Odsouhlaseno'], rows, 4, fmtCastka(total));

    const prRows: string[][] = [];
    let prTotal = 0;
    groups.forEach((g) =>
      (g.vklady ?? [])
        .filter((v) => v.prijato)
        .forEach((v) => {
          prTotal += v.castka ?? 0;
          prRows.push([g.owner_name || g.owner_email, fmtDatum(v.datum), v.poznamka || '—', fmtCastka(v.castka), fmtDatumCas(v.prijato_at)]);
        }),
    );
    const prijateTable = prRows.length
      ? buildTableHtml('Přijaté peníze', ['Zaměstnanec', 'Datum', 'Poznámka', 'Částka', 'Potvrzeno'], prRows, 3, fmtCastka(prTotal))
      : '';

    const html = buildPrintDoc('Vyúčtování ke kontrole', utratyTable, prijateTable);
    downloadPdf(html, 'vyuctovani-ke-kontrole.pdf');
  }

  if (isLoading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--sd-doc-dim)' }}>Načítám…</div>;
  if (error) return <div style={{ padding: 24, borderRadius: 4, background: 'var(--sd-danger-soft)', color: 'var(--sd-danger)' }}>Chyba načítání.</div>;
  if (groups.length === 0) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--sd-doc-dim)' }}>
        Zatím vám nikdo nenasdílel žádné vyúčtování.
      </div>
    );
  }

  const inp: CSSProperties = { padding: '5px 8px', border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, fontSize: 13, width: '100%' };

  // počet položek, které ještě čekají na odsouhlasení účetní = „nevyřízené"
  const pendingCount = (g: SharedVyuctovani) => g.polozky.filter((p) => !p.schvaleno).length;

  const q = query.trim().toLowerCase();
  const filtered = groups.filter((g) => {
    if (q && !`${g.owner_name} ${g.owner_email}`.toLowerCase().includes(q)) return false;
    if (onlyPending && pendingCount(g) === 0) return false;
    return true;
  });
  // vybraný pracovník musí být i ve filtru, jinak ukážeme vše
  const selectedVisible = selected !== 'all' && filtered.some((g) => g.owner_id === selected);
  const mainGroups = selectedVisible ? filtered.filter((g) => g.owner_id === selected) : filtered;
  // Modal vkladů čte vklady z čerstvých dat (po akci se invaliduje query) — ne ze
  // zachyceného snapshotu, aby se stavy v seznamu rovnou aktualizovaly.
  const vkladForLive = vkladFor ? (groups.find((g) => g.owner_id === vkladFor.owner_id) ?? vkladFor) : null;

  return (
    <div style={{ display: 'flex', gap: 18, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      {/* ── Levý panel: hledání + filtr + seznam pracovníků ── */}
      <aside style={{ flex: '0 0 240px', minWidth: 220, display: 'flex', flexDirection: 'column', gap: 10, position: 'sticky', top: 12 }}>
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Hledat pracovníka…"
        />
        <Checkbox
          checked={onlyPending}
          onChange={(e) => setOnlyPending(e.target.checked)}
          label="Jen nevyřízené"
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <button
            type="button"
            onClick={() => setSelected('all')}
            style={{
              textAlign: 'left', padding: '8px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 13, fontWeight: 600,
              border: '1px solid', borderColor: selected === 'all' ? 'var(--sd-accent, #12325a)' : 'var(--sd-w-e7e1d4, #e7e1d4)',
              background: selected === 'all' ? 'var(--sd-accent-soft, #eaf0f8)' : '#fff', color: 'var(--sd-doc-ink)',
            }}
          >
            Všichni <span style={{ color: 'var(--sd-text-dim)' }}>· {filtered.length}</span>
          </button>
          {filtered.map((g) => {
            const pend = pendingCount(g);
            const active = selected === g.owner_id;
            return (
              <button
                key={g.owner_id}
                type="button"
                onClick={() => setSelected(g.owner_id)}
                style={{
                  textAlign: 'left', padding: '8px 10px', borderRadius: 4, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                  border: '1px solid', borderColor: active ? 'var(--sd-accent, #12325a)' : 'var(--sd-w-e7e1d4, #e7e1d4)',
                  background: active ? 'var(--sd-accent-soft, #eaf0f8)' : '#fff',
                }}
              >
                <span style={{ overflow: 'hidden' }}>
                  <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--sd-doc-ink)', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                    {g.owner_name || g.owner_email}
                  </span>
                  <span style={{ display: 'block', fontSize: 11.5, color: g.zustatek < 0 ? 'var(--sd-danger, #c0392b)' : 'var(--sd-text-dim)' }}>
                    zůstatek {fmtCastka(g.zustatek)}
                  </span>
                </span>
                {pend > 0 ? (
                  <Badge tone="yellow" pill>{pend}</Badge>
                ) : (
                  <Badge tone="success" pill dot>OK</Badge>
                )}
              </button>
            );
          })}
          {filtered.length === 0 && (
            <div style={{ padding: '10px', fontSize: 12.5, color: 'var(--sd-text-dim)', textAlign: 'center' }}>
              {onlyPending ? 'Vše vyřízeno 🎉' : 'Nikdo neodpovídá hledání.'}
            </div>
          )}
        </div>
      </aside>

      {/* ── Hlavní oblast: karty vybraného pracovníka (nebo všech) ── */}
      <div style={{ flex: 1, minWidth: 320, display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
          <Button variant="secondary" onClick={handlePdf}>📄 PDF</Button>
          <Button variant="secondary" onClick={() => downloadFile('/vyuctovani/shared-with-me/export.xlsx', 'vyuctovani-ke-kontrole.xlsx')}>⬇ Excel</Button>
        </div>
      {mainGroups.map((g) => (
        <div key={g.owner_id} style={{ border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, background: '#fff', overflow: 'hidden' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, padding: '10px 14px', background: 'var(--sd-w-f7f4ee, #f7f4ee)', borderBottom: '1px solid var(--sd-w-e7e1d4, #e7e1d4)' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{g.owner_name || g.owner_email}</div>
              {g.owner_name && <div style={{ fontSize: 12, color: 'var(--sd-text-dim)' }}>{g.owner_email}</div>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ textAlign: 'right', fontSize: 12.5, color: 'var(--sd-text-dim)' }}>
                <div>ověřeno <strong style={{ color: 'var(--sd-doc-ink)' }}>{g.pocet_overeno}/{g.pocet}</strong> · útraty <strong style={{ color: 'var(--sd-doc-ink)' }}>{fmtCastka(g.celkem)}</strong></div>
                <div>
                  vloženo (přijato) <strong style={{ color: 'var(--sd-doc-ink)' }}>{fmtCastka(g.vklady_celkem)}</strong> · zůstatek <strong style={{ color: g.zustatek < 0 ? 'var(--sd-danger, #c0392b)' : '#1f8b4c' }}>{fmtCastka(g.zustatek)}</strong>
                  {g.vklady_neprijate_celkem > 0 && <span style={{ color: 'var(--sd-warning, #b8860b)' }}> · čeká na přijetí <strong>{fmtCastka(g.vklady_neprijate_celkem)}</strong></span>}
                </div>
              </div>
              <Button variant="secondary" onClick={() => openVklad(g)}>+ Přidat peníze</Button>
            </div>
          </div>

          <UcetniUpozorneni group={g} />

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--sd-text-dim)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  <th style={{ padding: '6px 12px', width: 100 }}>Datum</th>
                  <th style={{ padding: '6px 12px', width: 150 }}>Stavba</th>
                  <th style={{ padding: '6px 12px' }}>Za co</th>
                  <th style={{ padding: '6px 12px', width: 120, textAlign: 'right' }}>Částka</th>
                  <th style={{ padding: '6px 12px', width: 130 }}>Sedí s dokladem</th>
                  <th style={{ padding: '6px 12px', width: 130 }}>Odsouhlaseno</th>
                  <th style={{ padding: '6px 12px', width: 90 }} />
                </tr>
              </thead>
              <tbody>
                {g.polozky.map((p) => (
                  <tr key={p.id} style={{ borderTop: '1px solid var(--sd-w-efeae0, #efeae0)', background: p.schvaleno ? '#f3faf5' : undefined }}>
                    <td style={{ padding: '8px 12px', fontFamily: 'var(--sd-font-mono)', color: 'var(--sd-text-dim)' }}>{fmtDatum(p.datum)}</td>
                    <td style={{ padding: '8px 12px', color: 'var(--sd-text-dim)' }}>{p.stavba || '—'}</td>
                    <td style={{ padding: '8px 12px' }}>{p.popis || '—'}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'var(--sd-font-mono)', fontWeight: 600 }}>{fmtCastka(p.castka, p.mena)}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <Checkbox
                        checked={p.overeno}
                        disabled={setOvereni.isPending}
                        onChange={(e) => setOvereni.mutate({ id: p.id, overeno: e.target.checked })}
                        label={p.overeno ? 'Ověřeno' : 'Označit'}
                      />
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <Checkbox
                        checked={p.schvaleno}
                        disabled={setSchvaleni.isPending}
                        onChange={(e) => setSchvaleni.mutate({ id: p.id, schvaleno: e.target.checked })}
                        label={p.schvaleno ? '🔒 Schváleno' : 'Odsouhlasit'}
                      />
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                        <button
                          type="button"
                          onClick={() => openEdit(p)}
                          title="Upravit (jako účetní)"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 15 }}
                        >
                          ✏️
                        </button>
                        <button
                          type="button"
                          onClick={() => navrhnoutSmazaniPolozky(p)}
                          disabled={navrhSmazPolozka.isPending}
                          title={p.smazani_navrzeno ? 'Stáhnout návrh smazání' : 'Navrhnout smazání (potvrdí zaměstnanec)'}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, opacity: p.smazani_navrzeno ? 0.5 : 1 }}
                        >
                          🗑️
                        </button>
                      </div>
                      {p.smazani_navrzeno && (
                        <div style={{ fontSize: 10.5, color: 'var(--sd-warning, #b8860b)', marginTop: 2 }}>čeká na potvrzení</div>
                      )}
                      {p.smazani_odmitnuto && (
                        <div style={{ fontSize: 10.5, color: 'var(--sd-danger, #c0392b)', marginTop: 2 }} title={p.smazani_duvod || undefined}>smazání odmítnuto</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {(() => {
            const prijate: PrijemRadek[] = (g.vklady ?? [])
              .filter((v) => v.prijato)
              .map((v) => ({ key: `v${v.id}`, datum: v.datum, castka: v.castka, od: 'Účetní / firma', poznamka: v.poznamka, prijato_at: v.prijato_at, vklad: v }))
              .sort((a, b) => (a.datum < b.datum ? 1 : a.datum > b.datum ? -1 : 0));
            if (prijate.length === 0) return null;
            return (
              <div style={{ borderTop: '1px solid var(--sd-w-e7e1d4, #e7e1d4)', padding: '10px 14px 4px', background: 'var(--sd-w-fbfaf6, #fbfaf6)' }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: '#1f8b4c', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                  Přijaté peníze
                </div>
                <PrijatePenizeTable
                  rows={prijate}
                  renderAction={(r) => {
                    const v = r.vklad;
                    if (!v) return null;
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <button
                          type="button"
                          onClick={() => navrhnoutSmazaniVkladu(v)}
                          disabled={navrhSmazVklad.isPending}
                          title={v.smazani_navrzeno ? 'Stáhnout návrh smazání' : 'Navrhnout smazání (potvrdí zaměstnanec)'}
                          style={{ background: 'none', border: 'none', color: 'var(--sd-danger, #c0392b)', cursor: 'pointer', fontWeight: 700, fontSize: 15, opacity: v.smazani_navrzeno ? 0.5 : 1 }}
                        >
                          🗑️
                        </button>
                        {v.smazani_navrzeno && <span style={{ fontSize: 10.5, color: 'var(--sd-warning, #b8860b)' }}>čeká na potvrzení</span>}
                        {v.smazani_odmitnuto && <span style={{ fontSize: 10.5, color: 'var(--sd-danger, #c0392b)' }} title={v.smazani_duvod || undefined}>odmítnuto</span>}
                      </div>
                    );
                  }}
                />
              </div>
            );
          })()}

          <PrevodyKontrola
            odchozi={g.prevody_odchozi}
            prichozi={g.prevody_prichozi}
            ownerLabel={g.owner_name || g.owner_email}
            onOverit={(id, overeno) => setPrevodOvereni.mutate({ prevodId: id, overeno })}
            pending={setPrevodOvereni.isPending}
          />
        </div>
      ))}

      <Modal
        open={!!edit}
        onClose={() => setEdit(null)}
        title="Upravit útratu (účetní)"
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setEdit(null)}>Zrušit</Button>
            <Button variant="primary" onClick={saveEdit} disabled={updateUcetni.isPending}>
              {updateUcetni.isPending ? 'Ukládám…' : 'Uložit změny'}
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--sd-text-dim)' }}>Datum
            <input type="date" value={eDatum} onChange={(e) => setEDatum(e.target.value)} style={inp} />
          </label>
          <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--sd-text-dim)' }}>Stavba
            <input type="text" value={eStavba} onChange={(e) => setEStavba(e.target.value)} placeholder="které stavby se týká" style={inp} />
          </label>
          <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--sd-text-dim)' }}>Za co
            <input type="text" value={ePopis} onChange={(e) => setEPopis(e.target.value)} style={inp} />
          </label>
          <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--sd-text-dim)' }}>Částka (Kč)
            <input type="text" inputMode="decimal" value={eCastka} onChange={(e) => setECastka(e.target.value)} style={{ ...inp, textAlign: 'right', fontFamily: 'var(--sd-font-mono)' }} />
          </label>
        </div>
      </Modal>

      <Modal
        open={!!vkladFor}
        onClose={() => setVkladFor(null)}
        title={vkladFor ? `Přidat firemní peníze — ${vkladFor.owner_name || vkladFor.owner_email}` : 'Přidat peníze'}
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setVkladFor(null)}>Zrušit</Button>
            <Button variant="primary" onClick={saveVklad} disabled={addVklad.isPending || !vCastka.trim()}>
              {addVklad.isPending ? 'Ukládám…' : 'Vložit peníze'}
            </Button>
          </>
        }
      >
        {vkladFor && (
          <div style={{ fontSize: 13, color: 'var(--sd-text-dim)', marginBottom: 12 }}>
            Aktuální zůstatek: <strong style={{ color: vkladFor.zustatek < 0 ? 'var(--sd-danger, #c0392b)' : '#1f8b4c' }}>{fmtCastka(vkladFor.zustatek)}</strong>
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--sd-text-dim)' }}>Částka (Kč) *
            <input type="text" inputMode="decimal" value={vCastka} onChange={(e) => setVCastka(e.target.value)} placeholder="např. 5000" style={{ ...inp, textAlign: 'right', fontFamily: 'var(--sd-font-mono)' }} />
          </label>
          <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--sd-text-dim)' }}>Datum
            <input type="date" value={vDatum} onChange={(e) => setVDatum(e.target.value)} style={inp} />
          </label>
          <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--sd-text-dim)' }}>Poznámka
            <input type="text" value={vPoznamka} onChange={(e) => setVPoznamka(e.target.value)} placeholder="např. záloha červen" style={inp} />
          </label>
        </div>
        {vkladForLive && vkladForLive.vklady.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', color: 'var(--sd-text-dim)', marginBottom: 6 }}>Dosavadní vklady</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {vkladForLive.vklady.map((v) => (
                <div key={v.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12.5, padding: '4px 8px', background: 'var(--sd-w-f7f4ee, #f7f4ee)', borderRadius: 4 }}>
                  <span>
                    {fmtDatum(v.datum)} · <strong>{fmtCastka(v.castka)}</strong>{v.poznamka ? ` · ${v.poznamka}` : ''}
                    {v.odmitnuto && v.duvod_odmitnuti && <span style={{ color: 'var(--sd-danger, #c0392b)' }}> · odmítl: {v.duvod_odmitnuti}</span>}
                    {v.smazani_odmitnuto && v.smazani_duvod && <span style={{ color: 'var(--sd-danger, #c0392b)' }}> · smazání odmítl: {v.smazani_duvod}</span>}
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {v.odmitnuto ? (
                      <Badge tone="danger" pill dot>Odmítnuto zaměstnancem</Badge>
                    ) : v.smazani_navrzeno ? (
                      <Badge tone="yellow" pill>Čeká na potvrzení smazání</Badge>
                    ) : v.prijato ? (
                      <Badge tone="success" pill dot>Přijato zaměstnancem</Badge>
                    ) : (
                      <Badge tone="neutral" pill>Čeká na přijetí</Badge>
                    )}
                    {v.prijato ? (
                      // Přijatý vklad smí účetní jen NAVRHNOUT smazat — potvrdí zaměstnanec.
                      <button type="button" onClick={() => navrhnoutSmazaniVkladu(v)} disabled={navrhSmazVklad.isPending}
                        title={v.smazani_navrzeno ? 'Stáhnout návrh smazání' : 'Navrhnout smazání (potvrdí zaměstnanec)'}
                        style={{ background: 'none', border: 'none', color: 'var(--sd-danger, #c0392b)', cursor: 'pointer', fontWeight: 700, opacity: v.smazani_navrzeno ? 0.5 : 1 }}>🗑️</button>
                    ) : (
                      // Nepřijatý (i odmítnutý) vklad může účetní smazat rovnou.
                      <button type="button" onClick={() => removeVklad.mutate(v.id)} disabled={removeVklad.isPending} title="Smazat vklad" style={{ background: 'none', border: 'none', color: 'var(--sd-danger, #c0392b)', cursor: 'pointer', fontWeight: 700 }}>✕</button>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>
      </div>
    </div>
  );
}

// ─── UPOZORNĚNÍ PRO ÚČETNÍ (odmítnutí zaměstnancem s důvodem) ─────────────────
// Zaměstnanec mohl odmítnout přijetí vkladu nebo navržené smazání — tady účetní
// vidí, co a proč bylo odmítnuto, aby mohla zareagovat (opravit/znovu navrhnout).

function UcetniUpozorneni({ group }: { group: SharedVyuctovani }) {
  type Upoz = { key: string; text: string; duvod: string };
  const upoz: Upoz[] = [
    ...(group.vklady ?? []).filter((v) => v.odmitnuto).map((v) => ({
      key: `vo${v.id}`, text: `Odmítl příjem peněz ${fmtCastka(v.castka)}${v.poznamka ? ` (${v.poznamka})` : ''}`, duvod: v.duvod_odmitnuti,
    })),
    ...(group.vklady ?? []).filter((v) => v.smazani_odmitnuto).map((v) => ({
      key: `vs${v.id}`, text: `Odmítl smazání vkladu ${fmtCastka(v.castka)}${v.poznamka ? ` (${v.poznamka})` : ''}`, duvod: v.smazani_duvod,
    })),
    ...(group.polozky ?? []).filter((p) => p.smazani_odmitnuto).map((p) => ({
      key: `ps${p.id}`, text: `Odmítl smazání útraty „${p.popis || '—'}" (${fmtCastka(p.castka)})`, duvod: p.smazani_duvod,
    })),
    ...(group.prevody_odchozi ?? []).filter((p) => p.odmitnuto).map((p) => ({
      key: `po${p.id}`, text: `${p.to_name || p.to_email} odmítl/a převod ${fmtCastka(p.castka)} (částka se vrátila)`, duvod: p.duvod_odmitnuti,
    })),
  ];
  if (upoz.length === 0) return null;
  return (
    <div style={{ padding: '10px 14px', borderBottom: '1px solid #f3d2cd', background: 'var(--sd-danger-soft, #fdecea)' }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--sd-danger, #b33636)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
        Odmítnuto zaměstnancem
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {upoz.map((u) => (
          <div key={u.key} style={{ fontSize: 12.5 }}>
            <strong>{u.text}</strong>
            {u.duvod ? <span style={{ color: 'var(--sd-text-dim)' }}> — důvod: {u.duvod}</span> : <span style={{ color: 'var(--sd-text-dim)' }}> — bez uvedení důvodu</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── PŘEVODY V POHLEDU ÚČETNÍ ────────────────────────────────────────────────
// Účetní vidí, co zaměstnanec předal kolegům (odchozí — může zkontrolovat) i co
// od kolegů přijal (příchozí — jen informativně, je už oboustranně potvrzené).

function PrevodyKontrola({
  odchozi, prichozi, ownerLabel, onOverit, pending,
}: {
  odchozi: VyuctovaniPrevod[];
  prichozi: VyuctovaniPrevod[];
  ownerLabel: string;
  onOverit: (id: number, overeno: boolean) => void;
  pending: boolean;
}) {
  if (odchozi.length === 0 && prichozi.length === 0) return null;
  return (
    <div style={{ borderTop: '1px solid var(--sd-w-e7e1d4, #e7e1d4)', padding: '10px 14px', background: 'var(--sd-w-fbfaf6, #fbfaf6)' }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--sd-text-dim)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
        Převody mezi kolegy
      </div>

      {odchozi.length > 0 && (
        <div style={{ marginBottom: prichozi.length > 0 ? 12 : 0 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--sd-doc-ink)', marginBottom: 4 }}>↗ {ownerLabel} předal/a</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {odchozi.map((p) => (
              <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', fontSize: 12.5, padding: '6px 8px', background: '#fff', border: '1px solid var(--sd-w-eee6d6, #eee6d6)', borderRadius: 4 }}>
                <span>
                  {fmtDatum(p.datum)} · <strong>{fmtCastka(p.castka)}</strong> · pro {p.to_name || p.to_email}
                  {p.poznamka ? ` · ${p.poznamka}` : ''}
                  {p.odmitnuto && p.duvod_odmitnuti && <span style={{ color: 'var(--sd-danger, #c0392b)' }}> · odmítl: {p.duvod_odmitnuti}</span>}
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {p.odmitnuto ? (
                    <Badge tone="danger" pill dot>Odmítnuto příjemcem</Badge>
                  ) : p.prijato ? (
                    <Badge tone="success" pill dot>Přijato příjemcem</Badge>
                  ) : (
                    <Badge tone="neutral" pill>Čeká na přijetí</Badge>
                  )}
                  {!p.odmitnuto && (
                    <Checkbox
                      checked={p.overeno}
                      disabled={pending}
                      onChange={(e) => onOverit(p.id, e.target.checked)}
                      label={p.overeno ? 'Zkontrolováno' : 'Označit'}
                    />
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {prichozi.length > 0 && (
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--sd-doc-ink)', marginBottom: 4 }}>↙ {ownerLabel} přijal/a</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {prichozi.map((p) => (
              <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', fontSize: 12.5, padding: '6px 8px', background: '#fff', border: '1px solid var(--sd-w-eee6d6, #eee6d6)', borderRadius: 4 }}>
                <span>{fmtDatum(p.datum)} · <strong>{fmtCastka(p.castka)}</strong> · od {p.from_name || p.from_email}{p.poznamka ? ` · ${p.poznamka}` : ''}</span>
                <Badge tone="success" pill dot>Přijato</Badge>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
